import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { OfferHistoryAction, OfferHistoryEntry } from '../../types/offers';
import { formatDubaiInstant } from './offerEditModel';

const ACTION_LABEL: Record<OfferHistoryAction, string> = {
  OFFER_APPROVED: 'Approved',
  OFFER_REJECTED: 'Rejected',
  OFFER_SUSPENDED: 'Suspended',
  OFFER_ENDED: 'Ended',
  OFFER_EDITED: 'Edited',
};

const ACTION_BADGE: Record<OfferHistoryAction, string> = {
  OFFER_APPROVED: 'approved',
  OFFER_REJECTED: 'rejected',
  OFFER_SUSPENDED: 'rejected',
  OFFER_ENDED: 'neutral',
  OFFER_EDITED: 'update-requested',
};

export function actorName(entry: Pick<OfferHistoryEntry, 'adminSubjectId' | 'adminName'>): string {
  if (entry.adminSubjectId === 'system') return 'Garagat (automatic)';
  return entry.adminName?.trim() || 'Admin';
}

const shown = (v: string | null | undefined) => (v == null || v === '' ? '—' : v);

/** #997 — the offer's admin action log (`GET …/offers/{id}/history`), newest first. */
export default function OfferActivityCard({ offerId, token, refreshKey }: { offerId: string; token: string; refreshKey?: number }) {
  const [entries, setEntries] = useState<OfferHistoryEntry[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    api.getOfferHistory(offerId, token, 50)
      .then((rows) => { if (!cancelled) setEntries(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [offerId, token, refreshKey]);

  return (
    <div className="admin-card" data-testid="offer-activity">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Activity</h3>
      </div>
      {error && <div className="admin-info-banner danger" role="alert">Could not load the activity.</div>}
      {!error && entries == null && <div className="admin-text-muted" role="status">Loading activity…</div>}
      {entries?.length === 0 && <div className="admin-text-muted">No admin actions yet.</div>}
      {entries && entries.length > 0 && (
        <ol className="offer-activity">
          {entries.map((e) => (
            <li key={e.id} className="offer-activity__item" data-testid="offer-activity-item">
              <div className="offer-activity__head">
                <span className={`admin-badge ${ACTION_BADGE[e.action] ?? 'neutral'}`}>{ACTION_LABEL[e.action] ?? e.action}</span>
                <span className="offer-activity__who">{actorName(e)}</span>
                <time className="offer-activity__when" dateTime={e.occurredAt}>{formatDubaiInstant(e.occurredAt)}</time>
              </div>
              {e.reason && e.action !== 'OFFER_EDITED' && <div className="offer-activity__reason">{e.reason}</div>}
              {e.changes && e.changes.length > 0 && (
                <ul className="offer-activity__changes">
                  {e.changes.map((c) => (
                    <li key={c.field}>
                      <span className="offer-activity__label">{c.label}:</span>{' '}
                      <s className="offer-activity__before">{shown(c.before)}</s>
                      {' → '}
                      <strong className="offer-activity__after">{shown(c.after)}</strong>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
